import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Trash2, Plus } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";

const CURRENCIES = ["AUD", "USD", "NZD", "EUR"];

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setForm(data));
  }, []);

  if (!form) {
    return <div className="p-10 text-[#5B6B7C]">Loading settings…</div>;
  }

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        default_gst_rate: Number(form.default_gst_rate) || 0,
        quote_counter: Number(form.quote_counter) || 1000,
        exclusion_library: (form.exclusion_library || [])
          .map((s) => s.trim())
          .filter(Boolean),
      };
      const { data } = await api.put("/settings", payload);
      setForm(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 sm:p-8 lg:p-10 max-w-3xl mx-auto pb-24">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.35em] text-[#0088CC] mb-2">
          Configuration
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#011836]">
          Settings
        </h1>
        <p className="mt-2 text-sm text-[#5B6B7C]">
          Company details and quoting defaults.
        </p>
        {!isAdmin && (
          <div className="mt-3 text-xs text-[#5B6B7C] border border-[#DDE5EC] bg-white px-3 py-2 rounded inline-block">
            Read only — admin role required to edit settings.
          </div>
        )}
      </div>

      <div className="sos-surface rounded-lg p-6 space-y-5" data-testid="settings-form">
        <div>
          <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Company Name</Label>
          <Input
            value={form.company_name}
            disabled={!isAdmin}
            onChange={(e) => setForm({ ...form, company_name: e.target.value })}
            className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
            data-testid="company-name-input"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">ABN / Tax ID</Label>
            <Input
              value={form.abn || ""}
              disabled={!isAdmin}
              onChange={(e) => setForm({ ...form, abn: e.target.value })}
              className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Phone</Label>
            <Input
              value={form.phone || ""}
              disabled={!isAdmin}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
            />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
              Email (reply-to for all proposal emails)
            </Label>
            <Input
              type="email"
              value={form.email || ""}
              disabled={!isAdmin}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
            />
            <div className="text-[11px] text-[#64748B] mt-1 leading-relaxed">
              When a client hits Reply on any Email Proposal, their response
              lands in this inbox — regardless of which staff member clicked
              Send.
            </div>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Address</Label>
            <Input
              value={form.address || ""}
              disabled={!isAdmin}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
            />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
              Proposal &ldquo;From&rdquo; address
            </Label>
            <Input
              type="email"
              value={form.sender_email || ""}
              disabled={!isAdmin}
              placeholder="quotes@yourdomain.com"
              onChange={(e) => setForm({ ...form, sender_email: e.target.value })}
              className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC] font-mono text-sm"
              data-testid="settings-sender-email"
            />
            <div className="text-[11px] text-[#64748B] mt-1 leading-relaxed">
              Address the Email Proposal flow sends from. Must be on a domain
              verified at Resend, otherwise the send will be rejected. Leave
              blank to fall back to the deployment&apos;s default sender.
            </div>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
              Email template style
            </Label>
            <select
              value={form.email_template_style || "executive"}
              disabled={!isAdmin}
              onChange={(e) => setForm({ ...form, email_template_style: e.target.value })}
              className="mt-1.5 w-full bg-white border border-[#DDE5EC] text-[#011836] rounded-md h-10 px-3 focus:outline-none focus:ring-2 focus:ring-[#0088CC]"
              data-testid="settings-email-template-style"
            >
              <option value="executive">Executive &mdash; blue gradient header, prominent CTA</option>
              <option value="nautical">Nautical &mdash; centred logo, navy band, full-width CTA</option>
              <option value="editorial">Editorial &mdash; magazine layout, serif headline, pill CTA</option>
            </select>
            <div className="text-[11px] text-[#64748B] mt-1 leading-relaxed">
              Visual treatment for the proposal email you send to clients. Preview each in
              a new tab:{" "}
              <a href="/_email_preview_executive.html" target="_blank" rel="noopener" className="text-[#0088CC] hover:underline">Executive</a>
              {" · "}
              <a href="/_email_preview_nautical.html" target="_blank" rel="noopener" className="text-[#0088CC] hover:underline">Nautical</a>
              {" · "}
              <a href="/_email_preview_editorial.html" target="_blank" rel="noopener" className="text-[#0088CC] hover:underline">Editorial</a>
            </div>
          </div>
        </div>

        <div className="border-t border-[#DDE5EC] pt-5">
          <h2 className="font-display text-base font-semibold text-[#011836] mb-4">Quoting Defaults</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                Default Currency
              </Label>
              <Select
                value={form.default_currency}
                onValueChange={(v) => setForm({ ...form, default_currency: v })}
                disabled={!isAdmin}
              >
                <SelectTrigger
                  className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836]"
                  data-testid="default-currency-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#DDE5EC] text-[#011836]">
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                Default GST rate (0.10 = 10%)
              </Label>
              <Input
                type="number"
                step="0.01"
                value={form.default_gst_rate}
                disabled={!isAdmin}
                onChange={(e) => setForm({ ...form, default_gst_rate: e.target.value })}
                className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
              />
            </div>
            <div className="flex items-center justify-between sm:col-span-2 border border-[#DDE5EC] rounded-md px-4 py-3 bg-white">
              <div>
                <div className="text-sm text-[#011836]">GST enabled by default</div>
                <div className="text-xs text-[#5B6B7C]">
                  Pre-toggle GST on new quotes.
                </div>
              </div>
              <Switch
                checked={form.default_gst_enabled}
                disabled={!isAdmin}
                onCheckedChange={(v) => setForm({ ...form, default_gst_enabled: v })}
                data-testid="default-gst-toggle"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                Quote Number Prefix
              </Label>
              <Input
                value={form.quote_prefix}
                disabled={!isAdmin}
                onChange={(e) => setForm({ ...form, quote_prefix: e.target.value })}
                className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                Quote Counter
              </Label>
              <Input
                type="number"
                value={form.quote_counter}
                disabled={!isAdmin}
                onChange={(e) => setForm({ ...form, quote_counter: e.target.value })}
                className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
              />
            </div>
          </div>
        </div>

        {/* Proposal defaults — used by the multi-page PDF export.
            Each quote can override the payment terms + T&Cs on a
            per-quote basis; the exclusion library is the single
            tick-box source of truth. */}
        <div className="border-t border-[#DDE5EC] pt-5">
          <h2 className="font-display text-base font-semibold text-[#011836] mb-1">Proposal Defaults</h2>
          <p className="text-xs text-[#5B6B7C] mb-4">
            These values populate every new quote&apos;s PDF export. Individual quotes can override the
            Payment Terms, T&amp;Cs and About-SOS blocks; the exclusion library below feeds the
            tick-box picker in the Quote editor.
          </p>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                Payment Terms
              </Label>
              <Textarea
                value={form.default_payment_terms || ""}
                disabled={!isAdmin}
                onChange={(e) => setForm({ ...form, default_payment_terms: e.target.value })}
                className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] min-h-[110px] focus-visible:ring-[#0088CC]"
                data-testid="default-payment-terms"
                placeholder="One line per milestone, e.g. 10% deposit on signing"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                About SOS
              </Label>
              <Textarea
                value={form.default_about_sos || ""}
                disabled={!isAdmin}
                onChange={(e) => setForm({ ...form, default_about_sos: e.target.value })}
                className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] min-h-[100px] focus-visible:ring-[#0088CC]"
                data-testid="default-about-sos"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                General Terms &amp; Conditions
              </Label>
              <Textarea
                value={form.default_terms_conditions || ""}
                disabled={!isAdmin}
                onChange={(e) => setForm({ ...form, default_terms_conditions: e.target.value })}
                className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] min-h-[160px] focus-visible:ring-[#0088CC]"
                data-testid="default-terms-conditions"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                  Exclusion Library ({(form.exclusion_library || []).length})
                </Label>
                {isAdmin && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setForm({
                        ...form,
                        exclusion_library: [...(form.exclusion_library || []), ""],
                      })
                    }
                    className="h-7 px-2 border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5] hover:text-[#011836] text-xs"
                    data-testid="add-exclusion-btn"
                  >
                    <Plus className="h-3 w-3 mr-1" /> Add
                  </Button>
                )}
              </div>
              <div className="rounded border border-[#DDE5EC] bg-white divide-y divide-[#DDE5EC]">
                {(form.exclusion_library || []).map((ex, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 px-2 py-1.5"
                    data-testid={`exclusion-library-row-${i}`}
                  >
                    <Input
                      value={ex}
                      disabled={!isAdmin}
                      onChange={(e) => {
                        const next = [...form.exclusion_library];
                        next[i] = e.target.value;
                        setForm({ ...form, exclusion_library: next });
                      }}
                      className="flex-1 bg-transparent border-none text-[#011836] h-8 focus-visible:ring-0"
                      placeholder="Exclusion line"
                    />
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={() =>
                          setForm({
                            ...form,
                            exclusion_library: form.exclusion_library.filter((_, idx) => idx !== i),
                          })
                        }
                        className="text-[#5B6B7C] hover:text-red-400 shrink-0"
                        data-testid={`remove-exclusion-${i}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
                {(form.exclusion_library || []).length === 0 && (
                  <div className="px-3 py-2 text-xs text-[#5B6B7C]">
                    No exclusions in library — add some so they appear as tick-boxes in the Quote
                    Editor.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {isAdmin && (
          <div className="pt-4 flex justify-end">
            <Button
              onClick={save}
              disabled={saving}
              className="bg-[#0088CC] hover:bg-[#006DA6] text-white"
              data-testid="save-settings-btn"
            >
              {saving ? "Saving…" : "Save settings"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
